import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiCall from "../utils/apicall";
import { showSuccessToast, showErrorToast } from '../utils/Toast';

export const getJobs = createAsyncThunk("getJobs/jobs", async (ThunkApi) => {
  try {
    const res = await ApiCall.get("/job");
    return res.data.data.jobs;
  } catch (error) {
    return ThunkApi.rejectWithValue(error.response.data.message);
  }
});

export const createJob = createAsyncThunk(
  "createJob/jobs",
  async (data, ThunkApi) => {
    try {
      const res = await ApiCall.post("/job", data);
      return res.data;
    } catch (error) {
      const { message } = error.response.data;
      throw new Error(message);
    }
  }
);

export const updateJob = createAsyncThunk(
  "updateJobs/jobs",
  async (data, ThunkApi) => {
    try {
      const res = await ApiCall.put(`/job/${data.id}`, data);
      console.log(res);
      return res.data;
    } catch (error) {
      const { message } = error.response.data;
      throw new Error(message);
    }
  }
);

export const deleteJob = createAsyncThunk(
  "deleteJob/jobs",
  // Codex F4 fix 2026-05-07: accept either a bare id (legacy callers) OR
  // { id, reason } so the mandatory deletion reason validated by the
  // confirmation dialog actually reaches the backend audit_log. Without
  // this wiring the dialog collected the reason and discarded it — the
  // audit-trail claim shown in the UI was false. Backend captures the
  // reason from request body via verifyDocumentAccess.tryAuditBlock /
  // server-side delete handler (V-03 evidence-capture surface).
  async (arg, ThunkApi) => {
    const id = (arg && typeof arg === "object") ? arg.id : arg;
    const reason = (arg && typeof arg === "object") ? (arg.reason || "") : "";
    if (id == null) {
      return ThunkApi.rejectWithValue("deleteJob: id is required");
    }
    try {
      // axios DELETE supports body via { data: ... }
      const res = await ApiCall.delete(`/job/${id}`, {
        data: { reason },
      });
      if (res.data?.success) {
        return id;
      }
      // Codex WARNING fix: server replied 200 but success=false. Without this
      // explicit reject the thunk fulfilled with undefined and the UI showed
      // fake success.
      return ThunkApi.rejectWithValue(res.data?.message || "Server reported failure");
    } catch (error) {
      return ThunkApi.rejectWithValue(
        error?.response?.data?.message || error?.message || "Failed to delete job"
      );
    }
  }
);

export const importJob = createAsyncThunk(
  "ImportJob/jobs",
  async (data, thunkAPI) => {
    try {
      const res = await ApiCall.post("/job/import", data, {
        headers: {
          "Content-Type": "application/json", // Set the content type to indicate JSON data
        },
      });
      return res.data.data;
    } catch (error) {
      // Review-fix (F-05): was `return error` -> .fulfilled then spread a non-iterable
      // Error ([...action.payload]) -> TypeError crashed the reducer. Reject cleanly instead.
      return thunkAPI.rejectWithValue(error?.response?.data?.message || error?.message || "Import failed");
    }
  }
);

export const jobSlice = createSlice({
  name: "jobs",
  initialState: {
    jobs: [],
    isJobLoading: false,
    isSuccess: false,
  },
  extraReducers: (builder) => {
    builder.addCase(getJobs.pending, (state, action) => {
      state.isJobLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(getJobs.fulfilled, (state, action) => {
      // Codex WARNING fix: previously isSuccess=false on fulfilled, leaving
      // consumers unable to tell load-completed-successfully from load-failed.
      // Defensive: always store an array. A malformed/empty payload must never
      // leave `jobs` as undefined/object — that blanks the list + throws on .map.
      state.jobs = Array.isArray(action.payload) ? action.payload : [];
      state.isJobLoading = false;
      state.isSuccess = true;
    });
    builder.addCase(getJobs.rejected, (state, action) => {
      state.isJobLoading = false;
      state.isSuccess = false;
          showErrorToast('Failed to load jobs. Please refresh.');
    });
    builder.addCase(deleteJob.pending, (state, action) => {
      // console.log('in extra pending')
      state.isJobLoading = true;
    });
    builder.addCase(deleteJob.fulfilled, (state, action) => {
      state.jobs = state.jobs.filter((job) => job.id !== action.payload);
      state.isJobLoading = false;
          showSuccessToast('Job deleted successfully.');
    });
    builder.addCase(deleteJob.rejected, (state, action) => {
      state.isJobLoading = false;
          showErrorToast('Failed to delete job. Please try again.');
    });

    builder.addCase(createJob.pending, (state, action) => {
      // console.log('in extra add pending')
      state.isJobLoading = true;
    });
    builder.addCase(createJob.fulfilled, (state, action) => {
      state.jobs = [...state.jobs, action.payload];
      state.isJobLoading = false;
          showSuccessToast('Job created successfully.');
    });
    builder.addCase(createJob.rejected, (state, action) => {
      state.isJobLoading = false;
          showErrorToast('Failed to create job. Please try again.');
      return action.payload;
    });

    builder.addCase(updateJob.pending, (state, action) => {
      // console.log('in extra update pending')
      state.isJobLoading = true;
    });
    builder.addCase(updateJob.fulfilled, (state, action) => {
      const updatedJob = action.payload;
      state.jobs = state.jobs.map((job) => {
        if (job.id === updatedJob.id) {
          return updatedJob;
        }
        return job;
      });
      state.isJobLoading = false;
          showSuccessToast('Job updated successfully.');
    });
    builder.addCase(updateJob.rejected, (state, action) => {
      state.isJobLoading = false;
          showErrorToast('Failed to update job. Please try again.');
      return action.payload;
    });

    builder.addCase(importJob.pending, (state, action) => {
      // console.log('in extra add pending')
      state.isJobLoading = true;
    });
    builder.addCase(importJob.fulfilled, (state, action) => {
      // Review-fix (F-05): guard the spread — a non-array payload must not crash the reducer.
      const imported = Array.isArray(action.payload) ? action.payload : [];
      state.jobs = [...imported, ...state.jobs];
      state.isJobLoading = false;
          showSuccessToast('Jobs imported successfully.');
    });
    builder.addCase(importJob.rejected, (state, action) => {
      state.isJobLoading = false;
    });
  },
});

export default jobSlice.reducer;
